import React, { useState } from 'react';
import { Button, ButtonGroup, Grid2, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useGetUsers } from '@openapi/users/users';
import { useGetVenue } from '@openapi/venues/venues';
import useNotify from '../../hooks/useNotify';
import useSorting from '../../hooks/useSorting';
import { TABLE_ROW_COUNT_DEFAULT } from '../../helpers/constants';
import PageWithTitle from '../../components/atom/PageWithTitle';
import UsersTable from './components/UsersTable';
import SearchInput from '../../components/atom/SearchInput';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { getAssignableTemplates } from '../../helpers/capabilities';

const UsersPage = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { user: currentUser } = useAuth();
    const canAddUsers =
        currentUser?.isSuperAdmin ||
        getAssignableTemplates(currentUser?.capabilities, currentUser?.isSuperAdmin).length > 0;
    const { venueId } = useParams();
    const { notifyError } = useNotify();
    const [{ limit, page }, setParams] = useState({ limit: TABLE_ROW_COUNT_DEFAULT, page: 0 });
    const { sort, sortDir, setSorting } = useSorting(['created_at', 'ASC']);
    const [search, setSearch] = useState('');
    const navigate = useNavigate();
    const {
        data,
        isLoading: loading,
        _error,
        refetch: reloadData,
    } = useGetUsers(
        venueId ? Number(venueId) : undefined,
        {
            search,
            page,
            pageSize: limit,
            sort,
            sortDir,
        },
        {
            query: {
                onError: notifyError,
            },
        },
    );

    const {
        data: venue,
        isLoading: loadingVenue,
        error: _errorVenue,
    } = useGetVenue(Number(venueId), {
        query: {
            onError: notifyError,
        },
    });

    return (
        <PageWithTitle
            title={`${t('users.title')} (${loadingVenue ? '' : venue?.name || ''})`}
            inlineHeadline
            headline={[
                <Grid2
                    container
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    wrap="nowrap"
                    key="search-and-buttons"
                >
                    {!isMobile && (
                        <Grid2 sx={{ mr: 2 }}>
                            <SearchInput
                                search={search}
                                setSearch={setSearch}
                                setParams={setParams}
                                limit={limit}
                            />
                        </Grid2>
                    )}
                    {canAddUsers && (
                        <Grid2 sx={{ flexShrink: 0 }}>
                            <ButtonGroup variant="contained">
                                <Tooltip title={t('users.newUserTitle')}>
                                    <Button
                                        fullWidth
                                        onClick={() => {
                                            navigate(`/venues/${venueId}/users/new`);
                                        }}
                                    >
                                        <AddIcon />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid2>
                    )}
                </Grid2>,
            ]}
        >
            {isMobile && (
                <Grid2 size={{ xs: 12 }}>
                    <SearchInput
                        search={search}
                        setSearch={setSearch}
                        setParams={setParams}
                        limit={limit}
                        fullWidth
                    />
                </Grid2>
            )}
            <UsersTable
                rows={
                    currentUser?.isSuperAdmin
                        ? data?.users
                        : data?.users?.filter((u) => !u.isSuperAdmin)
                }
                limit={limit}
                page={page}
                setParams={setParams}
                total={data?.total}
                loading={loading}
                reloadData={reloadData}
                sort={sort}
                sortDir={sortDir}
                setSorting={setSorting}
                venueId={venueId}
            />
        </PageWithTitle>
    );
};

export default UsersPage;
